import React, { useState } from "react";
import { COMPONENT_TYPES } from "../../../../pages/constants/AiAgent";
import { ComponentActionButton } from "./ComponentActionButton";
import { useComponentAction } from "./useComponentAction";
import { HiOutlineMapPin, HiOutlineClock, HiOutlineUsers } from "react-icons/hi2";

const MetaRow = ({ job }) => {
  const parts = [];
  if (job.created_at) parts.push(`Posted: ${job.created_at}`);
  if (job.recruiter_name) parts.push(`Recruiter: ${job.recruiter_name}`);
  if (parts.length === 0) return null;
  return <p className="text-xs text-gray-500 mt-1">{parts.join(" · ")}</p>;
};

const JobCardCompact = ({ job, onAction, actionsDisabled }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-300 transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-primaryBlack truncate">
          {job.index}. {job.title}
        </p>
        <MetaRow job={job} />
      </div>
      <div className="flex gap-1.5 ml-2 flex-shrink-0">
        {job.actions?.map((action) => (
          <ComponentActionButton
            key={action.action}
            {...action}
            onAction={(a, p) => onAction(a, p)}
            disabled={actionsDisabled}
          />
        ))}
      </div>
    </div>
  </div>
);

const JobCardMidDetail = ({ job, onAction, actionsDisabled }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-300 transition-colors">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-primaryBlack">
          {job.index}. {job.title}
        </p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-textGray">
          {job.location && (
            <span className="flex items-center gap-1">
              <HiOutlineMapPin className="w-3 h-3" /> {job.location}
            </span>
          )}
          {job.experience && (
            <span className="flex items-center gap-1">
              <HiOutlineClock className="w-3 h-3" /> {job.experience}
            </span>
          )}
          {job.positions && (
            <span className="flex items-center gap-1">
              <HiOutlineUsers className="w-3 h-3" /> {job.positions} position{job.positions !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <MetaRow job={job} />
      </div>
    </div>
    <div className="flex gap-1.5 mt-2">
      {job.actions?.map((action) => (
        <ComponentActionButton
          key={action.action}
          {...action}
          onAction={(a, p) => onAction(a, p)}
          disabled={actionsDisabled}
        />
      ))}
    </div>
  </div>
);

const JobCardFullDetail = ({ job, onAction, actionsDisabled }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-300 transition-colors">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-primaryBlack">
        {job.index}. {job.title}
      </p>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-textGray">
        {job.location && (
          <span className="flex items-center gap-1">
            <HiOutlineMapPin className="w-3 h-3" /> {job.location}
          </span>
        )}
        {job.experience && (
          <span className="flex items-center gap-1">
            <HiOutlineClock className="w-3 h-3" /> {job.experience}
          </span>
        )}
        {job.positions && (
          <span className="flex items-center gap-1">
            <HiOutlineUsers className="w-3 h-3" /> {job.positions} position{job.positions !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      {job.skills && (
        <div className="flex flex-wrap gap-1 mt-2">
          {job.skills.split(",").map((skill, i) => (
            <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              {skill.trim()}
            </span>
          ))}
        </div>
      )}
      {job.applicants != null && (
        <p className="text-xs text-textGray mt-1.5">
          {job.applicants} applicant{job.applicants !== 1 ? "s" : ""}
        </p>
      )}
      <MetaRow job={job} />
    </div>
    <div className="flex gap-1.5 mt-2">
      {job.actions?.map((action) => (
        <ComponentActionButton
          key={action.action}
          {...action}
          onAction={(a, p) => onAction(a, p)}
          disabled={actionsDisabled}
        />
      ))}
    </div>
  </div>
);

const variantCardMap = {
  compact: JobCardCompact,
  mid_detail: JobCardMidDetail,
  full_detail: JobCardFullDetail,
};

const PAGE_SIZE = 5;

const JobListComponent = ({ payload, disabled }) => {
  const { variant = "mid_detail", data, actions, text } = payload;
  const { sendAction } = useComponentAction();
  const [actionsDisabled, setActionsDisabled] = useState(disabled);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const CardComponent = variantCardMap[variant] || JobCardMidDetail;
  const allJobs = data?.jobs || [];
  const visibleJobs = allJobs.slice(0, visibleCount);
  const hasMore = visibleCount < allJobs.length;

  const handleAction = (action, params) => {
    setActionsDisabled(true);
    sendAction(COMPONENT_TYPES.JOB_LIST, action, params);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {text && (
        <p className="text-sm text-gray-700 whitespace-pre-line">{text}</p>
      )}
      {visibleJobs.map((job) => (
        <CardComponent
          key={job.job_id}
          job={job}
          onAction={handleAction}
          actionsDisabled={actionsDisabled}
        />
      ))}
      {hasMore && (
        <button
          className="text-sm text-web-blue hover:text-blue-700 font-medium py-1.5 transition-colors"
          onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
        >
          + View more ({allJobs.length - visibleCount} remaining)
        </button>
      )}
      {actions?.length > 0 && (
        <div className="flex gap-1.5 mt-1">
          {actions.map((action) => (
            <ComponentActionButton
              key={action.action}
              {...action}
              onAction={handleAction}
              disabled={actionsDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default JobListComponent;
